import { EditorView } from "@codemirror/view";
import { HighlightStyle, syntaxHighlighting } from "@codemirror/language";
import { tags } from "@lezer/highlight";

/**
 * Structural theme for the studio markdown editor.
 * Uses DaisyUI CSS custom properties so it adapts to dark/light mode automatically.
 */
export const studioTheme = EditorView.theme({
  "&": {
    fontFamily: "'Inter', ui-sans-serif, system-ui, sans-serif",
    outline: "none",
  },
  "&.cm-focused": {
    outline: "none",
  },
  ".cm-content": {
    padding: "1rem",
    lineHeight: "1.625",
    fontSize: "0.875rem",
    caretColor: "oklch(var(--s))",
  },
  ".cm-cursor, .cm-dropCursor": {
    borderLeftColor: "oklch(var(--s))",
  },
  "&.cm-focused .cm-selectionBackground, .cm-selectionBackground": {
    background: "oklch(var(--s) / 0.20) !important",
  },
  ".cm-activeLine": {
    background: "oklch(var(--b2) / 0.5)",
  },
  ".cm-gutters": {
    display: "none",
  },
  ".cm-placeholder": {
    color: "oklch(var(--bc) / 0.3)",
  },
});

/**
 * Scoped syntax highlighting — ONLY styles markdown syntax markers,
 * never the content text itself.
 *
 * This avoids conflicts with live preview decorations which own all
 * content styling (headings, bold, italic, code, links, etc.).
 * When the cursor enters a block and live preview decorations are removed,
 * these faded markers become visible, giving a clean raw editing experience.
 *
 * What this styles (syntax markers — hidden by live preview):
 *   #, ##, ###, **, *, `, ```, >, -, 1., [ ], [x], [, ](url)
 *
 * What this does NOT style (content — owned by live preview):
 *   heading text, bold text, italic text, code content, link text,
 *   blockquote text, list item text
 */
const highlightStyle = HighlightStyle.define([
  // Bold/italic — these are formatting (weight/style), not visual chrome,
  // so they apply in both raw and preview modes without conflicting.
  { tag: tags.strong, fontWeight: "bold" },
  { tag: tags.emphasis, fontStyle: "italic" },

  // Markdown syntax markers — faded copper so they recede visually
  // These are the characters that live preview hides: #, **, *, `, >, etc.
  { tag: tags.processingInstruction, color: "oklch(var(--s) / 0.35)" },
  { tag: tags.meta, color: "oklch(var(--s) / 0.35)" },

  // URL in links — faded when visible in raw mode
  { tag: tags.url, color: "oklch(var(--s) / 0.4)" },
]);

export const studioHighlighting = syntaxHighlighting(highlightStyle);
